<template>
  <div>
    <b-container fluid class="pb-4 px-0">
      <template v-if="apiResponse.success">
        <template v-if="items.length">
          <!-- Toolbar -->
          <div class="dir-toolbar">
            <div class="dir-toolbar-search">
              <el-input
                v-model="searchQuery"
                placeholder="Buscar contacto, email, teléfono..."
                suffix-icon="el-icon-search"
                size="small"
                clearable
              />
            </div>
            <el-select
              v-model="orderByQuery"
              placeholder="Ordenar"
              size="small"
              class="dir-toolbar-select"
            >
              <el-option label="Recientes" value="recent" />
              <el-option label="Nombre A-Z" value="a-z" />
              <el-option label="Nombre Z-A" value="z-a" />
            </el-select>
            <div class="dir-toolbar-right">
              <ExportButtons
                :data="filteredContacts"
                :columns="exportColumns"
                filename="listado_contactos"
              />
              <button
                class="dir-view-toggle"
                :class="{ active: viewMode === 'grid' }"
                @click="viewMode = 'grid'"
              >
                <i class="el-icon-menu"></i>
              </button>
              <button
                class="dir-view-toggle"
                :class="{ active: viewMode === 'list' }"
                @click="viewMode = 'list'"
              >
                <i class="el-icon-s-unfold"></i>
              </button>
            </div>
          </div>

          <!-- Grid view -->
          <div v-if="viewMode === 'grid'" class="dir-grid-contacts">
            <div
              v-for="(item, index) in filteredContacts"
              :key="item.id || index"
              class="dir-contact-card"
              @click="onOpenDialogEditComponent(item)"
            >
              <div class="dir-card-header">
                <div
                  class="dir-card-avatar"
                  :style="{ background: getAvatarColor(index) }"
                >
                  {{ getInitials(item.name) }}
                </div>
                <div style="flex: 1; min-width: 0;">
                  <p class="dir-card-name">{{ item.name }}</p>
                  <p v-if="item.position" class="dir-contact-position">
                    {{ item.position }}
                  </p>
                </div>
              </div>

              <div v-if="item.email" class="dir-card-meta">
                <i class="el-icon-message"></i>{{ item.email }}
              </div>
              <div v-if="item.phone" class="dir-card-meta">
                <i class="el-icon-phone-outline"></i>{{ item.phone }}
              </div>
              <div
                v-if="item.client && item.client.name"
                class="dir-contact-business"
              >
                <i class="el-icon-office-building"></i>{{ item.client.name }}
              </div>
            </div>

            <!-- Skeleton cards while loading next batch -->
            <template v-if="loadingMore">
              <div
                v-for="i in 4"
                :key="'sk-g-' + i"
                class="dir-contact-card dir-card--skeleton"
              >
                <div class="dir-card-header">
                  <div class="sk-circle"></div>
                  <div style="flex:1;min-width:0">
                    <div class="sk-bar" style="width:65%"></div>
                    <div
                      class="sk-bar sk-bar--sm"
                      style="width:38%;margin-top:6px"
                    ></div>
                  </div>
                </div>
                <div
                  class="sk-bar sk-bar--sm"
                  style="width:82%;margin-top:14px"
                ></div>
                <div
                  class="sk-bar sk-bar--sm"
                  style="width:58%;margin-top:8px"
                ></div>
              </div>
            </template>
          </div>

          <!-- List view (tabla horizontal) -->
          <div v-else class="ct-table">
            <div class="ct-table-header">
              <span>Contacto</span>
              <span>Cargo</span>
              <span>Email</span>
              <span>Teléfono</span>
              <span>Empresa</span>
            </div>
            <div
              v-for="(item, index) in filteredContacts"
              :key="item.id || 'list-' + index"
              class="ct-table-row"
              @click="onOpenDialogEditComponent(item)"
            >
              <span class="ct-td-name">
                <div
                  class="ct-row-avatar"
                  :style="{ background: getAvatarColor(index) }"
                >
                  {{ getInitials(item.name) }}
                </div>
                <span class="ct-row-name-text">{{ item.name }}</span>
              </span>
              <span class="ct-td-position">{{ item.position || "—" }}</span>
              <span class="ct-td-email">{{ item.email || "—" }}</span>
              <span class="ct-td-phone">{{ item.phone || "—" }}</span>
              <span class="ct-td-business">{{
                item.client && item.client.name ? item.client.name : "—"
              }}</span>
            </div>

            <!-- Skeleton rows while loading next batch -->
            <template v-if="loadingMore">
              <div
                v-for="i in 4"
                :key="'sk-l-' + i"
                class="ct-table-row ct-row--skeleton"
              >
                <span class="ct-td-name">
                  <div class="sk-circle sk-circle--sm"></div>
                  <div class="sk-bar" style="width:110px"></div>
                </span>
                <span class="ct-td-position"
                  ><div class="sk-bar" style="width:70px"></div
                ></span>
                <span class="ct-td-email"
                  ><div class="sk-bar" style="width:130px"></div
                ></span>
                <span class="ct-td-phone"
                  ><div class="sk-bar" style="width:80px"></div
                ></span>
                <span class="ct-td-business"
                  ><div class="sk-bar" style="width:90px"></div
                ></span>
              </div>
            </template>

            <div
              v-if="!filteredContacts.length && !loadingMore"
              class="ct-table-empty"
            >
              Sin resultados
            </div>
          </div>
        </template>
        <EmptyPage
          icon="el-icon-user"
          text="Aún no has registrado contactos."
          v-else
        />
      </template>
      <SkeletonLoadingCard v-else />

      <!-- Scroll sentinel: triggers next page load when visible -->
      <div ref="scrollSentinel" class="ct-scroll-sentinel"></div>
    </b-container>

    <!-- Dialog edit contact -->
    <el-dialog
      :visible.sync="dialogEditContact"
      :before-close="handleCloseContactEdit"
      append-to-body
      custom-class="fullscreen-content-modal"
    >
      <EditContactComponent
        :contact="contact"
        :showBackIcon="false"
        v-if="dialogEditContact"
        @onSuccess="onSuccessContactEdit"
        @close="handleCloseContactEdit"
      />
    </el-dialog>
  </div>
</template>

<script>
import SkeletonLoadingCard from "@/components/Animations/Loading/SkeletonCard";
import EmptyPage from "../../components/EmptyPage";
import EditContactComponent from "./EditContact";
import businessService from "../services/businessService";

const AVATAR_COLORS = [
  "#6366f1",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#3fdbbf",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#06b6d4",
  "#31355c"
];

export default {
  name: "BusinessListContactComponent",
  data: () => ({
    dialogEditContact: false,
    contact: {},
    apiResponse: { success: false },
    searchQuery: "",
    orderByQuery: "recent",
    viewMode: "grid",
    items: [],
    hasMore: true,
    loadingPage: false,
    loadingMore: false,
    filters: { page: 1, search: null },
    searchTimer: null
  }),
  computed: {
    filteredContacts() {
      let contacts = this.items.concat();
      contacts.sort((a, b) => {
        switch (this.orderByQuery) {
          case "recent":
            return (b.id || 0) - (a.id || 0);
          case "a-z":
            return (a.name || "")
              .toLowerCase()
              .localeCompare((b.name || "").toLowerCase());
          case "z-a":
            return (b.name || "")
              .toLowerCase()
              .localeCompare((a.name || "").toLowerCase());
          default:
            return 0;
        }
      });
      return contacts;
    },
    exportColumns() {
      return [
        { label: "Nombre", field: "name" },
        { label: "Email", field: "email" },
        { label: "Teléfono", field: "phone" },
        { label: "Cargo", field: "position" },
        { label: "Empresa", value: i => (i.client ? i.client.name : "") },
        {
          label: "RUC/Documento empresa",
          value: i => (i.client ? i.client.document : "")
        }
      ];
    }
  },
  watch: {
    viewMode(val) {
      this.$store.dispatch("setViewPreference", {
        key: "contacts",
        value: val
      });
    },
    searchQuery() {
      this.onSearchDebounced();
    }
  },
  methods: {
    getInitials(name) {
      if (!name) return "??";
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    },
    getAvatarColor(index) {
      return AVATAR_COLORS[index % AVATAR_COLORS.length];
    },
    openAddDialog() {
      this.contact = {};
      this.dialogEditContact = true;
    },
    onOpenDialogEditComponent(contact) {
      this.dialogEditContact = true;
      this.contact = contact;
    },
    onSuccessContactEdit() {
      this.dialogEditContact = false;
      this.contact = {};
      this.fetchFirstPage();
    },
    handleCloseContactEdit() {
      this.dialogEditContact = false;
    },
    onSearchDebounced() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.filters.search = this.searchQuery;
        this.filters.page = 1;
        this.fetchFirstPage();
      }, 350);
    },
    async fetchFirstPage() {
      this.apiResponse.success = false;
      this.items = [];
      this.hasMore = true;
      this.loadingPage = false;
      this.loadingMore = false;
      try {
        const response = await businessService.getContacts({
          params: {
            page: 1,
            per_page: 20,
            search: this.filters.search || undefined
          }
        });
        if (response.data && response.data.contacts) {
          const { contacts, pagination } = response.data;
          this.items = contacts;
          // Emit real total so sidebar badge always shows the full count
          const total =
            pagination && pagination.total != null
              ? pagination.total
              : contacts.length;
          this.$emit("update-count", total);
          this.filters.page = 2;
          this.hasMore = !(pagination && 1 >= pagination.last_page);
        } else {
          this.hasMore = false;
        }
      } catch {
        this.hasMore = false;
      } finally {
        this.apiResponse.success = true;
      }
    },
    onWindowScroll() {
      if (!this.hasMore || this.loadingPage || !this.apiResponse.success)
        return;
      const sentinel = this.$refs.scrollSentinel;
      // offsetParent is null when the element (or an ancestor) has display:none
      if (!sentinel || !sentinel.offsetParent) return;
      const rect = sentinel.getBoundingClientRect();
      if (rect.top <= window.innerHeight + 400) {
        this.loadNextPage();
      }
    },
    async loadNextPage() {
      if (!this.hasMore || this.loadingPage) return;
      // Set synchronously (before any await) so rapid scroll events can't
      // slip through the guard above before this function yields.
      this.loadingPage = true;
      this.loadingMore = true;
      try {
        const response = await businessService.getContacts({
          params: {
            page: this.filters.page,
            per_page: 20,
            search: this.filters.search || undefined
          }
        });
        if (response.data && response.data.contacts) {
          const { contacts, pagination } = response.data;
          const existingIds = new Set(this.items.map(i => i.id));
          const newItems = contacts.filter(c => !existingIds.has(c.id));
          this.items = [...this.items, ...newItems];
          const done = pagination && this.filters.page >= pagination.last_page;
          if (!done) this.filters.page++;
          else this.hasMore = false;
        } else {
          this.hasMore = false;
        }
      } catch {
        // keep hasMore=true so user can retry by scrolling again
      } finally {
        this.loadingPage = false;
        this.loadingMore = false;
      }
    },
    dateNow() {
      return new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "_");
    },
    async downloadExcel() {
      this.loading = true;
      try {
        const response = await businessService.downloadContactsExcel();
        const { data, success } = response.data;
        if (success) {
          const date = this.dateNow();
          const anchor = document.createElement("a");
          const filename = `${date}_listado_contactos.xlsx`;
          anchor.setAttribute("download", filename);
          anchor.setAttribute("href", data);
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          this.$notifications.success({
            message: "Excel descargado exitosamente"
          });
        }
      } catch (error) {
        return false;
      } finally {
        this.loading = false;
      }
    },
    async downloadPdf() {
      this.loading = true;
      try {
        const response = await businessService.downloadContactsPdf();
        const { data, success } = response.data;
        if (success) {
          const date = this.dateNow();
          const anchor = document.createElement("a");
          const filename = `${date}_listado_contactos.pdf`;
          anchor.setAttribute("download", filename);
          anchor.setAttribute("href", data);
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          this.$notifications.success({
            message: "PDF descargado exitosamente"
          });
        }
      } catch (error) {
        return false;
      } finally {
        this.loading = false;
      }
    }
  },
  components: {
    SkeletonLoadingCard,
    EmptyPage,
    EditContactComponent
  },
  mounted() {
    const saved = this.$store.getters.getViewPreference("contacts");
    if (saved) this.viewMode = saved;
    this.fetchFirstPage();
    window.addEventListener("scroll", this.onWindowScroll, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onWindowScroll);
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$border: #e4eaf6;
$bg: #f7f8f9;

// ── Skeleton shimmer ──
@keyframes sk-shine {
  from {
    background-position: -400px 0;
  }
  to {
    background-position: 400px 0;
  }
}
.sk-bar,
.sk-circle {
  display: block;
  background: linear-gradient(90deg, #e9ebee 25%, #f3f3f3 50%, #e9ebee 75%);
  background-size: 800px 100%;
  animation: sk-shine 1.4s ease-in-out infinite;
  border-radius: 6px;
}
.sk-bar {
  height: 13px;
  &--sm {
    height: 10px;
  }
}
.sk-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  &--sm {
    width: 30px;
    height: 30px;
  }
}
.dir-card--skeleton {
  pointer-events: none;
  cursor: default !important;
}
.ct-row--skeleton {
  pointer-events: none;
  cursor: default !important;
  &:hover {
    background: transparent !important;
  }
}

.ct-scroll-sentinel {
  height: 1px;
  width: 100%;
}

.dir-toolbar .el-input__inner,
.dir-toolbar .el-select .el-input__inner {
  min-height: 34px !important;
  height: 34px;
  font-size: 13px;
  border-radius: 8px;
}

// ── LIST VIEW (tabla horizontal) ──
.ct-table {
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  overflow: hidden;
}
.ct-table-header {
  display: grid;
  grid-template-columns: 1fr 120px 180px 130px 160px;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: $bg;
  border-bottom: 1px solid $border;
  font-size: 11px;
  font-weight: 700;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.ct-table-row {
  display: grid;
  grid-template-columns: 1fr 120px 180px 130px 160px;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-bottom: 1px solid $border;
  font-size: 13px;
  color: #303133;
  cursor: pointer;
  transition: background 0.12s;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: $bg;
  }
}
.ct-td-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.ct-row-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}
.ct-row-name-text {
  font-weight: 600;
  color: $primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ct-td-position {
  font-size: 12px;
  color: #606266;
}
.ct-td-email {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ct-td-phone {
  font-size: 12px;
  color: #606266;
}
.ct-td-business {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ct-table-empty {
  padding: 40px;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}

@media (max-width: 1024px) {
  .ct-table-header,
  .ct-table-row {
    grid-template-columns: 1fr 120px 160px 120px;
  }
  .ct-td-business,
  .ct-table-header span:nth-child(5) {
    display: none;
  }
}
@media (max-width: 768px) {
  .ct-table-header,
  .ct-table-row {
    grid-template-columns: 1fr 130px;
  }
  .ct-td-position,
  .ct-td-phone,
  .ct-td-business,
  .ct-table-header span:nth-child(2),
  .ct-table-header span:nth-child(4),
  .ct-table-header span:nth-child(5) {
    display: none;
  }
}
</style>
